/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import * as EndpointConstants from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { ConfidentialInputPlaceHolder, FeatureAvailabilityFlags } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { ServiceEndpointsSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Source/ServiceEndpointsSource";
import { InputDescriptor, InputDataType, InputMode, InputValue, InputValidation } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { IServiceEndpointAuthenticationSchemeExtended, IEndpointAuthSchemeSelectionPanelState } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { FormItem } from "VSSUI/FormItem";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { TextField } from "VSSUI/TextField";
import { RadioButtonGroupDirection } from "VSSUI/Components/RadioButton/RadioButton.Props";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { IListBoxItem } from "VSSUI/ListBox";
import { Dropdown } from "VSSUI/Dropdown";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { Spinner, SpinnerSize, SpinnerOrientation } from "VSSUI/Spinner";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

import { DataSourceDetails, ResultTransformationDetails, ServiceEndpoint, ServiceEndpointRequest, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { Uri } from "VSS/Platform/Util/Url";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";


export function cleanEmptyValues(obj: { [key: string]: string; }) {
    for (const key in obj) {
        const value = obj[key];
        if (!value || value.trim() === "") {
            delete obj[key];
        }
    }
}

export function cloneEndpoint(endpoint: ServiceEndpoint): ServiceEndpoint {
    let clonedEndpoint: ServiceEndpoint = {
        authorization: {
            parameters: Object.assign({}, endpoint.authorization.parameters),
            scheme: endpoint.authorization.scheme
        },
        createdBy: Object.assign({}, endpoint.createdBy),
        data: Object.assign({}, endpoint.data),
        id: endpoint.id,
        isReady: endpoint.isReady,
        isDisabled: endpoint.isDisabled,
        isShared: endpoint.isShared,
        isOutdated: endpoint.isOutdated,
        name: endpoint.name,
        owner: endpoint.owner,
        type: endpoint.type,
        url: endpoint.url,
        administratorsGroup: endpoint.administratorsGroup,
        description: endpoint.description,
        groupScopeId: endpoint.groupScopeId,
        operationStatus: endpoint.operationStatus,
        readersGroup: endpoint.readersGroup,
        serviceEndpointProjectReferences: null
    };

    return clonedEndpoint;
}

export class EndpointsEditorPanelUtils {
    public static readonly SERVICE_ENDPOINTS_LEARN_MORE_LINK = "https://aka.ms/SCLearnMore";
    public static readonly AZURE_RM_NEED_HELP_LINK = "https://docs.microsoft.com/en-us/azure/devops/pipelines/library/service-endpoints?view=azure-devops&tabs=yaml#common-service-connection-types";
    public static readonly SERVICE_ENDPOINTS_TROUBLESHOOT_LINK = "https://aka.ms/TroubleshootServiceConnections";
    public static readonly AZURE_RM_LEARN_MORE_LINK = "https://docs.microsoft.com/en-us/azure/devops/pipelines/library/connect-to-azure?view=azure-devops";
    public static readonly AZURE_RM_TROUBLESHOOT_LINK = "https://go.microsoft.com/fwlink/?linkid=835898";
    public static readonly AZURE_RM_WORKLOAD_IDENTITY_FEDERATION_LEARN_MORE_LINK = "https://aka.ms/azdo-rm-workload-identity";
    public static readonly AZURE_RM_WORKLOAD_IDENTITY_FEDERATION_TROUBLESHOOT_LINK = "https://aka.ms/azdo-rm-workload-identity-troubleshooting";
    public static readonly DOCKER_LEARN_MORE_LINK = "https://aka.ms/DockerSCLearnMore";
    public static readonly DOCKER_TO_AZURE_CONTAINER_REGISTRY_LEARN_MORE_LINK = "https://learn.microsoft.com/azure/devops/pipelines/ecosystems/containers/publish-to-acr";
    public static _verificationStatus : number = Constants.VerificationStatus.NotClicked;

    public static getComponent
        (
            inputKey: string,
            inputDescriptor: InputDescriptor,
            currentValue: string,
            onValueChanged: any,
            error: string,
            isEditFlow: boolean,
            pageContext: IVssPageContext,
            configKey: DropdownSelection
        ): JSX.Element {
        let returnValue = null;
        if (inputDescriptor.values && inputDescriptor.values.isDisabled) {
            return (<div />);
        }

        let isDisabled = false;
        if (isEditFlow && inputDescriptor.properties && inputDescriptor.properties["updatesNotAllowed"]) {
            isDisabled = true;
        }

        switch (inputDescriptor.inputMode) {
            case InputMode.Combo:
                returnValue = this._getPickListComponent(inputKey, inputDescriptor, currentValue, onValueChanged, configKey, isDisabled);
                break;
            case InputMode.TextBox:
                returnValue = EndpointsEditorPanelUtils._getTextBoxComponent(inputKey, inputDescriptor, currentValue, onValueChanged, false, error, isEditFlow, pageContext, isDisabled);
                break;
            case InputMode.CheckBox:
                returnValue = EndpointsEditorPanelUtils._getCheckBoxInputComponent(inputKey, inputDescriptor, currentValue, onValueChanged, isDisabled, pageContext);
                break;
            case InputMode.PasswordBox:
                returnValue = EndpointsEditorPanelUtils._getTextBoxComponent(inputKey, inputDescriptor, currentValue, onValueChanged, false, error, isEditFlow, pageContext, isDisabled);
                break;
            case InputMode.RadioButtons:
                returnValue = EndpointsEditorPanelUtils._getRadioButtonsComponent(inputKey, inputDescriptor, currentValue, onValueChanged, isDisabled);
                break;
            case InputMode.TextArea:
                returnValue = EndpointsEditorPanelUtils._getTextBoxComponent(inputKey, inputDescriptor, currentValue, onValueChanged, true, error, isEditFlow, pageContext, isDisabled);
                break;
            case InputMode.None:
                break;
            default:
                returnValue = EndpointsEditorPanelUtils._getTextBoxComponent(inputKey, inputDescriptor, currentValue, onValueChanged, false, error, isEditFlow, pageContext, isDisabled);
                break;
        }

        return returnValue;
    }

    public static getComponentDefaultValue(inputDescriptor: InputDescriptor, component: JSX.Element) {
        switch (inputDescriptor.inputMode) {
            case InputMode.Combo:
            case InputMode.RadioButtons:
                return this._getSelectDefaultValue(inputDescriptor);
            case InputMode.CheckBox:
                return this._getCheckboxValue(component);
            default:
                return "";
        }
    }

    public static getVerifyButton(isDisabled: boolean, verifyEndpoint: any): React.ReactNode {
        return (<Button
                    disabled={ isDisabled }
                    text={ Resources.VerifyButtonText }
                    className="verify-button"
                    onClick={() => verifyEndpoint()}
                />);
    }

    public static getVerificationDetails(isVerifyInProgress: boolean, isVerificationSuccess?: boolean, errorMessage?: string): React.ReactNode {
        if (isVerifyInProgress) {
            this._verificationStatus = Constants.VerificationStatus.InProgress;
            return (<Spinner ariaLive="assertive" label={Resources.VerifyingSpinnerLabel} orientation={SpinnerOrientation.row} className="verifying-spinner padding-left-8" size={SpinnerSize.medium} />);
        }

        if (isVerificationSuccess) {
            if (this._verificationStatus !== Constants.VerificationStatus.Successful) {
                Utils_Accessibility.announce(Resources.VerificationSucceeded);
                this._verificationStatus = Constants.VerificationStatus.Successful;
            }

            return (<div className="flex-row padding-left-8">
                        <Status
                            {...Statuses.Success}
                            key="success"
                            size={StatusSize.m}
                            className="flex-self-center "
                        />
                        <span className="padding-left-4 flex-self-center verification-success">{Resources.VerificationSucceeded}</span>
                    </div>);
        }
        else if (isVerificationSuccess === false) {
            if (this._verificationStatus !== Constants.VerificationStatus.Failed) {
                if (!!errorMessage) {
                    Utils_Accessibility.announce(Resources.VerificationFailed.concat(" ", errorMessage));
                    this._verificationStatus = Constants.VerificationStatus.Failed;
                }
            }

            return (<div className="flex-row padding-left-8">
                        <Status
                            {...Statuses.Failed}
                            key="failed"
                            size={StatusSize.m}
                            className="flex-self-center "
                        />
                        <span className="padding-left-4 flex-self-center verification-failure">{Resources.VerificationFailed}</span>
                    </div>);
        }
    }

    public static getServiceEndpointRequestForTest(serviceEndpoint: ServiceEndpoint): ServiceEndpointRequest {
        let dataSourceDetails: DataSourceDetails = {
            dataSourceName: Constants.TestConnectionDataSource,
            dataSourceUrl: "",
            headers: null,
            resourceUrl: "",
            requestContent: null,
            requestVerb: null,
            parameters: null,
            resultSelector: "",
            initialContextTemplate: ""
        };

        let resultTransformationDetails: ResultTransformationDetails = {
            callbackContextTemplate: "",
            callbackRequiredTemplate: "",
            resultTemplate: ""
        };

        return {
                dataSourceDetails: dataSourceDetails,
                resultTransformationDetails: resultTransformationDetails,
                serviceEndpointDetails: serviceEndpoint
            };
    }

    public static isInputValid(value: string | boolean | number, inputValidation: InputValidation, isVisible: boolean = true): boolean {
        let isOptional = !inputValidation || !inputValidation.isRequired || !isVisible;
        if (!value && !isOptional) {
            return false;
        } else if (!value && isOptional) {
            return true;
        }

        if (!inputValidation) {
            return true;
        }

        let validIntRegex = /^[0-9]+$/;
        switch (inputValidation.dataType) {
            case InputDataType.Boolean:
                return (value.toString().toLowerCase() === "true" || value.toString().toLowerCase() === "false") ? true : false;
            case InputDataType.Guid:
                return Utils_String.isGuid(value.toString());
            case InputDataType.Number:
                return validIntRegex.test(value.toString());
            case InputDataType.Uri:
                return EndpointsEditorPanelUtils.isEndpointUrlValid(value.toString());
            default:
                return true;
        }
    }

    public static isEndpointUrlValid(endpointUri: string): boolean {
        const uri = new Uri(endpointUri);
        if (uri && !!uri.scheme && !!uri.host) {
            return true;
        }

        return false;
    }

    public static isEndpointNameValid(endpointName: string): boolean {
        let validNameRegex = /^[0-9a-zA-Z-_.]+$/;
        return validNameRegex.test(endpointName);
    }

    public static getInvalidInputErrorMessage(inputValidation: InputValidation): string {
        if (!inputValidation) {
            return "";
        }

        switch (inputValidation.dataType) {
            case InputDataType.Boolean:
                return Resources.InvalidBooleanInput;
            case InputDataType.Guid:
                return Resources.InvalidGuidInput;
            case InputDataType.Number:
                return Resources.InvalidNumberInput;
            case InputDataType.Uri:
                return Resources.InvalidUriInput;
            default:
                return Resources.InvalidInput;
        }
    }

    public static isAuthSelectorPanelSupported(pageContext: IVssPageContext, endpointType: string): boolean {
        if (!endpointType) {
            return false;
        }

        if (endpointType.toUpperCase() !== EndpointConstants.ServiceEndpointTypes.AzureRM.toUpperCase()) {
            return false;
        }

        return !isFeatureFlagEnabled(pageContext, FeatureAvailabilityFlags.EnableNewARMServiceEndpointType, false);
    }

    public static copyToClipboard(text: string) {
        const listener = (e: ClipboardEvent) => {
            if (e.clipboardData) {
                e.clipboardData.setData("text/plain", text);
                e.preventDefault();
            }
        };

        // Add a listener to the copy event, and add it to the clipboard. Then remove the handler immediately, as it prevents default
        document.addEventListener("copy", listener);
        document.execCommand("copy");
        document.removeEventListener("copy", listener);
    }

    public static getLearnMoreLinkForAuthSchemeSelectionPanel(endpointType: string): string {
        let learnMoreLink: string = "";
        if (endpointType && endpointType.toUpperCase() === EndpointConstants.ServiceEndpointTypes.AzureRM.toUpperCase()) {
            learnMoreLink = EndpointsEditorPanelUtils.AZURE_RM_NEED_HELP_LINK;
        }

        return learnMoreLink;
    }

    public static getPageHeadingForAuthSchemeSelectionPanel(endpointType: string): string {
        let pageHeading: string = "";
        if (endpointType && endpointType.toUpperCase() === EndpointConstants.ServiceEndpointTypes.AzureRM.toUpperCase()) {
            pageHeading = Resources.ChooseAzureRMSchemeTitle;
        }

        return pageHeading;
    }

    public static getPageDescriptionForAuthSchemeSelectionPanel(endpointType: string): string {
        let pageDescription: string = "";
        if (endpointType && endpointType.toUpperCase() === EndpointConstants.ServiceEndpointTypes.AzureRM.toUpperCase()) {
            pageDescription = Resources.ChooseAzureRMSchemeHeading;

        }

        return pageDescription;
    }

    public static getExtendedAuthSchemesForEndpoint(pageContext: IVssPageContext, endpointType: ServiceEndpointType): IServiceEndpointAuthenticationSchemeExtended[] {
        if (endpointType && endpointType.name.toUpperCase() === EndpointConstants.ServiceEndpointTypes.AzureRM.toUpperCase()) {
            const isHosted: boolean = this._isHostedEnvironment(pageContext);

            const servicePrincipalManualScheme = this._getDefaultAuthScheme();
                servicePrincipalManualScheme.scheme = EndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal;
                servicePrincipalManualScheme.displayName = Resources.AzureRMServicePrincipalManualScheme;
                servicePrincipalManualScheme.creationMode = EndpointConstants.AzureEndpointConstants.SpnCreationModeManual;
                servicePrincipalManualScheme.pageHeading = Resources.ChooseAzureRMSchemeTitle;
                servicePrincipalManualScheme.pageDescription = Resources.CreateAzureRMServicePrincipalManualHeading;

            if (isHosted) {
                const availableSchemes = [];

                const oidcFederationScheme = endpointType.authenticationSchemes.find(x => x.scheme === EndpointConstants.EndpointAuthorizationSchemes.WorkloadIdentityFederation);
                if (oidcFederationScheme) {
                    const automaticOidcScheme = this._getDefaultAuthScheme();
                    automaticOidcScheme.scheme = EndpointConstants.EndpointAuthorizationSchemes.WorkloadIdentityFederation;
                    automaticOidcScheme.displayName =  Resources.AzureRMOidcFederationAutomaticScheme;
                    automaticOidcScheme.creationMode = EndpointConstants.AzureEndpointConstants.SpnCreationModeAutomatic;
                    automaticOidcScheme.pageHeading = Resources.ChooseAzureRMSchemeTitle;
                    automaticOidcScheme.pageDescription = Resources.CreateAzureRMOidcFederationAutomaticHeading;
                    automaticOidcScheme.isRecommended = true;

                    availableSchemes.push(automaticOidcScheme);

                    const manualOidcScheme = this._getDefaultAuthScheme();
                    manualOidcScheme.scheme = EndpointConstants.EndpointAuthorizationSchemes.WorkloadIdentityFederation;
                    manualOidcScheme.displayName =  Resources.AzureRMOidcFederationManualScheme;
                    manualOidcScheme.creationMode = EndpointConstants.AzureEndpointConstants.SpnCreationModeManual;
                    manualOidcScheme.pageHeading = Resources.ChooseAzureRMSchemeTitle;
                    manualOidcScheme.pageDescription = Resources.CreateAzureRMOidcFederationManualHeading;
                    manualOidcScheme.allowDraft = true;
                    availableSchemes.push(manualOidcScheme);
                }

                const servicePrincipalAutomaticScheme = this._getDefaultAuthScheme();
                servicePrincipalAutomaticScheme.scheme = EndpointConstants.EndpointAuthorizationSchemes.ServicePrincipal;
                servicePrincipalAutomaticScheme.displayName = Resources.AzureRMServicePrincipalAutomaticScheme;
                if (!oidcFederationScheme) {
                    servicePrincipalAutomaticScheme.isRecommended = true;
                }
                servicePrincipalAutomaticScheme.creationMode = EndpointConstants.AzureEndpointConstants.SpnCreationModeAutomatic;
                servicePrincipalAutomaticScheme.pageHeading = Resources.ChooseAzureRMSchemeTitle;
                servicePrincipalAutomaticScheme.pageDescription = Resources.CreateAzureRMServicePrincipalAutomaticHeading;
                availableSchemes.push(servicePrincipalAutomaticScheme);

                availableSchemes.push(servicePrincipalManualScheme);

                const managedIdentityScheme = this._getDefaultAuthScheme();
                managedIdentityScheme.scheme = EndpointConstants.EndpointAuthorizationSchemes.ManagedServiceIdentity;
                managedIdentityScheme.displayName = Resources.AzureRMServicePrincipalManagedIdentityScheme;
                managedIdentityScheme.pageHeading = Resources.ChooseAzureRMSchemeTitle;
                managedIdentityScheme.pageDescription = Resources.CreateAzureRMManagedIdentityHeading;
                availableSchemes.push(managedIdentityScheme);


                const isPublishProfileFeatureEnabled = isFeatureFlagEnabled(pageContext,
                    Constants.FeatureAvailabilityFlags.EnablePublishProfileAuthSchemeInARMEndpoint, false);
                if (isPublishProfileFeatureEnabled) {
                    const publishProfileScheme = this._getDefaultAuthScheme();
                    publishProfileScheme.scheme = EndpointConstants.EndpointAuthorizationSchemes.PublishProfile;
                    publishProfileScheme.displayName = Resources.AzureRMPublishProfileScheme;
                    publishProfileScheme.pageHeading = Resources.ChooseAzureRMSchemeTitle;
                    publishProfileScheme.pageDescription = Resources.CreateAzureRMPublishProfileHeading;
                    availableSchemes.push(publishProfileScheme);
                }

                return availableSchemes;
            } else {
                return [
                    servicePrincipalManualScheme
                ];
            }
        }

        return [];
    }

    private static _isHostedEnvironment(pageContext: IVssPageContext): boolean {
        if (pageContext) {
            const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
            if (vssPageService) {
                const vssPageData = vssPageService.getData();
                return vssPageData.isHosted;
            }
        }

        return undefined;
    }

    private static _getDefaultAuthScheme(): IServiceEndpointAuthenticationSchemeExtended {
        return ({
            authorizationHeaders: [],
            authorizationUrl: undefined,
            clientCertificates: [],
            dataSourceBindings: [],
            displayName: null,
            inputDescriptors: [],
            scheme: null,
            isRecommended: false,
            pageHeading: "",
            pageDescription: "",
            requiresOAuth2Configuration: true,
            properties: {},
            allowDraft: false
        });
    }

    private static _getCheckboxValue(component: JSX.Element): boolean {
        return component.props.checked;
    }

    private static _getSelectDefaultValue(inputDescriptor: InputDescriptor): string {
        return inputDescriptor.values && inputDescriptor.values.defaultValue;
    }

    public static getInputValidation(
        dataType: InputDataType,
        isRequired?: boolean,
        maxLength?: number,
        maxValue?: number,
        minLength?: number,
        minValue?: number,
        pattern?: string,
        patternMismatchErrorMessage?: string): InputValidation {
        return ({
            dataType: dataType,
            isRequired: isRequired !== undefined ? isRequired : true,
            maxLength: maxLength ? maxLength : 200,
            maxValue: maxValue ? maxValue : null,
            minLength: minLength ? minLength : null,
            minValue: minValue ? minValue : null,
            pattern: pattern ? pattern : "",
            patternMismatchErrorMessage: patternMismatchErrorMessage ? patternMismatchErrorMessage : ""
        });
    }

    private static _getPickListComponent(inputKey: string, inputDescriptor: InputDescriptor, currentValue: string, onValueChanged: any, configKey: DropdownSelection, isDisabled: boolean): JSX.Element {
        let label = EndpointsEditorPanelUtils._getInputLabel(inputDescriptor);
        if (!currentValue && inputDescriptor.values) {
            currentValue = inputDescriptor.values.defaultValue;
        }

        let options: IListBoxItem[] = [];
        if (inputDescriptor.values && inputDescriptor.values.possibleValues) {
            inputDescriptor.values.possibleValues.map((item: InputValue, index) => {
                options.push({
                    id: item.value,
                    text: item.displayValue
                });
                if (item.value === currentValue) {
                    configKey.select(index);
                }
            });
        }

        return (
            <FormItem
                label={label}
                className="default-input-margin-top"
            >
                <Dropdown
                    ariaLabel={label}
                    items={options}
                    onSelect={(event: React.SyntheticEvent<HTMLElement>, selection?: IListBoxItem) => {
                        if (selection) {
                            onValueChanged(null, selection.id);
                        }
                    }}
                    selection={configKey}
                    className="endpoint-editor-panel-pick-list"
                    disabled={isDisabled}
                />
            </FormItem>);
    }

    private static _getCheckBoxInputComponent(inputKey: string, inputDescriptor: InputDescriptor, currentValue: string, onValueChanged: any, isDisabled: boolean, pageContext: IVssPageContext): JSX.Element {
        let label = EndpointsEditorPanelUtils._getInputLabel(inputDescriptor);
        let currentBooleanValue;
        try {
            currentBooleanValue = currentValue ? JSON.parse(currentValue.toLowerCase()) : false;
        }
        catch (e) {
            currentBooleanValue = currentValue ? true : false;
        }

         /* Some of the input descriptions contain mark down links. */
        /* Hence we need to render them as html. The strings are being normalized to remove any un-wanted content like 'script' tags. */
        /* tslint:disable:react-no-dangerous-html */
        return (
            <FormItem>
            <Checkbox
                key={inputKey}
                className="endpoint-editor-panel-check-box"
                label={label}
                checked={currentBooleanValue}
                onChange={onValueChanged}
                disabled={isDisabled}
                ariaLabel={label + ". " + (EndpointsEditorPanelUtils._normalizeHTML(inputDescriptor.description, pageContext) ?
                EndpointsEditorPanelUtils._normalizeHTML(inputDescriptor.description, pageContext).replace(/<[^>]+>/g, "") : "")}
            />
            <span
                className="secondary-text fontSizeS"
                dangerouslySetInnerHTML={{ __html: EndpointsEditorPanelUtils._normalizeHTML(inputDescriptor.description, pageContext) }}>
            </span>
        </FormItem>);
    }

    private static _getRadioButtonsComponent(inputKey: string, inputDescriptor: InputDescriptor, currentValue: string, onValueChanged: any, isDisabled: boolean): JSX.Element {
        let selectedIndex = 0;
        let label = EndpointsEditorPanelUtils._getInputLabel(inputDescriptor);
        inputDescriptor.values.possibleValues.forEach((possibleValue: any, index: number) => {
            if (possibleValue.value === currentValue) {
                selectedIndex = index;
            }
        });
        let direction = RadioButtonGroupDirection.Vertical;
        let className = "";
        if (inputDescriptor.values.possibleValues.length <= 2) {
            direction = RadioButtonGroupDirection.Horizontal;
            className = "radio-button-horizontal-spacing";
        }

        return (
            <RadioButtonGroup
                key={inputKey}
                onSelect={(selectionId: string) => {
                    onValueChanged(null, inputDescriptor.values.possibleValues[parseInt(selectionId.split(":")[1])].value);
                }}
                direction={direction}
                selectedButtonId={inputKey + ":" + selectedIndex.toString()}
                text={label}
            >
                {
                    inputDescriptor.values.possibleValues.map((possibleValue: any, index: number) => {
                        return (
                            <RadioButton className={className} key={possibleValue.value} id={inputKey + ":" + index.toString()} disabled={isDisabled}>
                                <span className="bolt-radio-button-label">{possibleValue.displayValue}</span>
                            </RadioButton>
                        );
                    })
                }
            </RadioButtonGroup>);
    }

    private static _getTextBoxComponent(inputKey: string, inputDescriptor: InputDescriptor, currentValue: string, onValueChanged: any, isMultiline: boolean, error: string, isEditFlow: boolean, pageContext: IVssPageContext, isDisabled: boolean): JSX.Element {
        let label = EndpointsEditorPanelUtils._getInputLabel(inputDescriptor);
        let placeholder = isEditFlow && inputDescriptor.isConfidential ? ConfidentialInputPlaceHolder : "";
        let maxLength = 20480;
        if (inputDescriptor.validation && inputDescriptor.validation.maxLength) {
            maxLength = inputDescriptor.validation.maxLength;
        } else if (isMultiline) {
            maxLength = undefined;
        }

        /* Some of the input descriptions contain mark down links. */
        /* Hence we need to render them as html. The strings are being normalized to remove any un-wanted content like 'script' tags. */
        /* tslint:disable:react-no-dangerous-html */
        return (
            <FormItem
                className={!error ? "endpoint-editor-panel-text-box" : "endpoint-editor-panel-text-box-error"}
                label={label}
                message={error}
                error={!!error}
                ariaLabel={label + ". " + (EndpointsEditorPanelUtils._normalizeHTML(inputDescriptor.description, pageContext) ?
                EndpointsEditorPanelUtils._normalizeHTML(inputDescriptor.description, pageContext).replace(/<[^>]+>/g, "") : "")}
            >
                <TextField
                    key={inputKey}
                    value={currentValue}
                    onChange={onValueChanged}
                    placeholder={placeholder}
                    inputType={inputDescriptor.isConfidential ? "password" : "text"}
                    multiline={isMultiline}
                    maxLength={maxLength}
                    disabled={isDisabled}
                />
                <span
                    className="secondary-text fontSizeS"
                    dangerouslySetInnerHTML={{ __html: EndpointsEditorPanelUtils._normalizeHTML(inputDescriptor.description, pageContext) }}>
                </span>
            </FormItem>
        );
        /* tslint:enable:react-no-dangerous-html */
    }

    private static _getInputLabel(inputDescriptor: InputDescriptor): string {
        let isOptional = !inputDescriptor.validation || !inputDescriptor.validation.isRequired;
        let label = isOptional ? inputDescriptor.name + Resources.OptionalInputSuffix : inputDescriptor.name;

        return label;
    }

    private static _normalizeHTML(content: string, pageContext: IVssPageContext): string {
        return ServiceEndpointsSource.getInstance(pageContext).normalizeHTML(content);
    }
}
